<script setup lang="ts">
import { computed, nextTick, onActivated, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNamespace } from '@zoryn/hooks'
import { getScene } from './catalog'
import {
  consumePendingNewChat,
  registerNewChatHandler,
  useAiChatNav
} from './composables/use-ai-chat-nav'
import { useAiClient, useAiHost } from './setup'
import ScopeBadge from './components/scope-badge.vue'
import PdfPreview from './components/pdf-preview.vue'
import type { SeekerCitation } from './types'

defineOptions({ name: 'ZAiChat' })

const ns = useNamespace('ai-chat')

interface MessageAttachment {
  id: string
  kind: 'file' | 'image' | 'audio'
  name: string
  size: number
  mime: string
  previewUrl?: string
  /** Durable object key in RustFS / MinIO (chat/…). */
  objectKey?: string
}

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  reasoning?: string
  citations?: SeekerCitation[]
  model?: string
  error?: boolean
  streaming?: boolean
  suggestions?: string[]
  suggestionsLoading?: boolean
  attachments?: MessageAttachment[]
}

interface ComposerAttachment extends MessageAttachment {
  file: File
}

type VoiceMode = 'idle' | 'listening' | 'recording'

type SpeechRecognitionLike = {
  lang: string
  continuous: boolean
  interimResults: boolean
  onresult: ((event: SpeechRecognitionEventLike) => void) | null
  onerror: ((event: { error?: string }) => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}

type SpeechRecognitionEventLike = {
  resultIndex: number
  results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }>
}

const route = useRoute()
const router = useRouter()
const client = useAiClient()
const host = useAiHost()
const {
  activeSceneId,
  chatResetKey,
  activeSessionId,
  conversations,
  externalUserId,
  refreshConversations,
  setActiveSessionId,
  clearShareIntent,
  shareIntentSessionId
} = useAiChatNav()
const input = ref('')
const sending = ref(false)
/** True while POST /v1/files/extract-text is in flight (before ask stream). */
const extracting = ref(false)
const extractingPreviewText = ref(false)
const imageZoom = ref(1)
const imagePanX = ref(0)
const imagePanY = ref(0)
const imagePanning = ref(false)
const imageStudioMoreOpen = ref(false)
let imagePanStartX = 0
let imagePanStartY = 0
let imagePanOriginX = 0
let imagePanOriginY = 0
const listRef = ref<HTMLElement | null>(null)
const composerRef = ref<HTMLTextAreaElement | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const imageInputRef = ref<HTMLInputElement | null>(null)
const sessionId = ref<string | null>(null)
const backendStatus = ref<string>('检测中…')
const activeRequestId = ref<string | null>(null)
const copiedId = ref<string | null>(null)
const editingId = ref<string | null>(null)
const moreOpenId = ref<string | null>(null)
const actionToast = ref('')
const attachments = ref<ComposerAttachment[]>([])
const previewAttachment = ref<MessageAttachment | null>(null)
const plusMenuOpen = ref(false)
const voiceMode = ref<VoiceMode>('idle')
const conversationTitle = ref<string | null>(null)
const loadingConversation = ref(false)
/** History / message multi-select share mode. */
const shareSelectMode = ref(false)
const selectedShareIds = ref<string[]>([])
const shareBarMoreOpen = ref(false)
const shareLinkModalOpen = ref(false)
const shareLinkCreating = ref(false)

let unsubscribeAsk: (() => void) | null = null
let scrollRaf = 0
let copiedTimer = 0
let toastTimer = 0
let recognition: SpeechRecognitionLike | null = null
let mediaRecorder: MediaRecorder | null = null
let mediaStream: MediaStream | null = null
let audioChunks: Blob[] = []
let voiceBaseText = ''
let voiceFinalText = ''
let bootstrapped = false
/** Dedupes handler + chatResetKey watch so startNewChat runs once per request. */
let lastHandledResetKey = -1
let extractAbort: AbortController | null = null
/** Set before abort(); used when AbortSignal.reason is unavailable. */
let extractAbortKind: 'timeout' | 'cancel' | null = null

const MAX_ATTACHMENTS = 6
const MAX_FILE_BYTES = 20 * 1024 * 1024
/** Frontend abort for extract-text; keep slightly under/aligned with SEEKER_MINERU_TIMEOUT_SECONDS. */
const EXTRACT_TIMEOUT_MS = 600_000
const DEFAULT_ATTACH_QUESTION = '请结合我上传的内容回答。'
const ATTACHMENT_NOTE_RE = /【已附带：([^】]*)】/g

const composerBusy = computed(() => sending.value || extracting.value)

const isImagePreview = computed(
  () => previewAttachment.value?.kind === 'image' && !!previewAttachment.value?.previewUrl
)

const imageStudioTools = [
  { id: 'mark', label: '标记' },
  { id: 'cutout', label: '抠图' },
  { id: 'erase', label: '擦除' },
  { id: 'expand', label: '扩图' },
  { id: 'enhance', label: '变清晰' },
  { id: 'video', label: '变视频' }
] as const

const scene = computed(() => getScene(activeSceneId.value))

const backend = computed(
  () => scene.value?.backend || { aclGroupIds: ['public', 'mes'] as string[] }
)

const scopeLabel = computed(() => scene.value?.scope.label || '企业公开知识')
const audited = computed(() => scene.value?.scope.audited ?? true)

const shareAllSelected = computed(() => {
  const ids = shareableMessages.value.map((m) => m.id)
  return ids.length > 0 && ids.every((id) => selectedShareIds.value.includes(id))
})

const shareSelectedCount = computed(() => selectedShareIds.value.length)

const shareableMessages = computed(() =>
  messages.value.filter(
    (m) =>
      (m.role === 'user' || m.role === 'assistant') &&
      !m.streaming &&
      !!(m.content?.trim() || m.attachments?.length)
  )
)

const title = computed(() => conversationTitle.value || scene.value?.title || '企业问答')
const subtitle = computed(
  () => scene.value?.summary || '在当前知识范围内提问，回答会标注可用来源边界。'
)

const messages = ref<Message[]>([])
const activeNavId = ref<string | null>(null)
const hoveredNavId = ref<string | null>(null)
const railExpanded = ref(false)

const userNavItems = computed(() =>
  messages.value
    .filter((m) => m.role === 'user')
    .map((m) => {
      const full = userMessagePlainText(m)
      return {
        id: m.id,
        summary: truncateText(full, 28),
        preview: truncateText(full, 120),
        truncated: full.length > 28
      }
    })
)

const showMsgRail = computed(() => userNavItems.value.length > 1)
const hoveredNavDetail = ref<string | null>(null)

function userMessagePlainText(msg: Message): string {
  const text = msg.content.replace(ATTACHMENT_NOTE_RE, '').replace(/\s+/g, ' ').trim()
  if (text) return text
  if (msg.attachments?.length) return msg.attachments.map((a) => a.name).join('、')
  return '（空消息）'
}

function truncateText(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text
}

function onNavItemEnter(
  item: { id: string; preview: string; truncated: boolean },
  event: MouseEvent | FocusEvent
): void {
  hoveredNavId.value = item.id
  const textEl = (event.currentTarget as HTMLElement | null)?.querySelector(
    '.msg-rail-text'
  ) as HTMLElement | null
  const overflowed = textEl
    ? textEl.scrollWidth > textEl.clientWidth + 1
    : item.truncated
  hoveredNavDetail.value = overflowed || item.truncated ? item.preview : null
}

function onNavItemLeave(): void {
  hoveredNavId.value = null
  hoveredNavDetail.value = null
}

function updateActiveFromScroll(): void {
  const root = listRef.value
  const items = userNavItems.value
  if (!root || !items.length) {
    activeNavId.value = null
    return
  }
  const marker = root.getBoundingClientRect().top + Math.min(96, root.clientHeight * 0.22)
  let current = items[0].id
  for (const item of items) {
    const el = root.querySelector(`[data-msg-id="${CSS.escape(item.id)}"]`) as HTMLElement | null
    if (!el) continue
    if (el.getBoundingClientRect().top <= marker) current = item.id
  }
  activeNavId.value = current
}

function onMessagesScroll(): void {
  updateActiveFromScroll()
}

function jumpToMessage(id: string): void {
  const el = listRef.value?.querySelector(`[data-msg-id="${CSS.escape(id)}"]`) as HTMLElement | null
  if (!el) return
  el.scrollIntoView({ block: 'start', behavior: 'smooth' })
  activeNavId.value = id
}

function openMsgRail(): void {
  railExpanded.value = true
}

function closeMsgRail(): void {
  railExpanded.value = false
  hoveredNavId.value = null
  hoveredNavDetail.value = null
}

function onRailFocusOut(event: FocusEvent): void {
  const root = event.currentTarget as HTMLElement | null
  const next = event.relatedTarget as Node | null
  if (root && next && root.contains(next)) return
  closeMsgRail()
}

watch(
  () => listRef.value,
  (el, prev) => {
    prev?.removeEventListener('scroll', onMessagesScroll)
    if (el) {
      el.addEventListener('scroll', onMessagesScroll, { passive: true })
      void nextTick(() => updateActiveFromScroll())
    }
  }
)

watch(userNavItems, async () => {
  await nextTick()
  updateActiveFromScroll()
})

/**
 * Keep session in nav state only. Writing ?session= into the URL changes
 * route.fullPath, which remounts keep-alive ChatPage and breaks「新建对话」.
 * Deep links still work once via the bootstrap watch below.
 */
function syncSessionQuery(id: string | null): void {
  setActiveSessionId(id)
  if (typeof route.query.session === 'string') {
    void router.replace({ path: route.path, query: {} })
  }
}

function revokeMessagePreviewUrls(list: Message[]): void {
  for (const msg of list) {
    for (const att of msg.attachments || []) {
      if (att.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(att.previewUrl)
    }
  }
}

function seedMessages(): void {
  editingId.value = null
  moreOpenId.value = null
  copiedId.value = null
  hoveredNavId.value = null
  activeNavId.value = null
  hoveredNavDetail.value = null
  railExpanded.value = false
  input.value = ''
  plusMenuOpen.value = false
  previewAttachment.value = null
  clearAttachments()
  void stopVoice(false, true)
  revokeMessagePreviewUrls(messages.value)
  const intro = scene.value?.prompt
    ? `已进入场景「${scene.value.title}」。${scene.value.prompt}`
    : '你好，我是屿枫。你可以询问生产、工艺、质量或设备相关问题。回答由内网 Seeker AI 生成。'
  messages.value = [
    {
      id: 'sys-1',
      role: 'system',
      content: `知识范围：${scopeLabel.value}${audited.value ? ' · 会话可审计' : ''} · ${backendStatus.value}`
    },
    {
      id: 'a-1',
      role: 'assistant',
      content: intro
    }
  ]
}

async function refreshBackendStatus(): Promise<void> {
  try {
    const health = await client.health()
    backendStatus.value = `已连接 ${health.baseUrl}`
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    backendStatus.value = `未连接后端（${msg}）`
  }
  if (messages.value[0]?.role === 'system') {
    messages.value[0] = {
      ...messages.value[0],
      content: `知识范围：${scopeLabel.value}${audited.value ? ' · 会话可审计' : ''} · ${backendStatus.value}`
    }
  }
}

async function startNewChat(): Promise<void> {
  exitShareSelectMode()
  await stopStreaming()
  sessionId.value = null
  conversationTitle.value = null
  seedMessages()
  syncSessionQuery(null)
  await refreshBackendStatus()
  await nextTick()
  composerRef.value?.focus()
}

async function openConversation(id: string): Promise<void> {
  if (loadingConversation.value || sending.value) return
  if (sessionId.value === id && messages.value.some((m) => m.role === 'user')) return
  loadingConversation.value = true
  try {
    await stopStreaming()
    previewAttachment.value = null
    revokeMessagePreviewUrls(messages.value)
    const [rows, baseUrl] = await Promise.all([
      client.getConversationMessages(id, externalUserId.value),
      client.getBaseUrl()
    ])
    const meta = conversations.value.find((c) => c.id === id)
    sessionId.value = id
    conversationTitle.value = meta?.title || '历史对话'
    syncSessionQuery(id)
    const restored: Message[] = [
      {
        id: 'sys-1',
        role: 'system',
        content: `知识范围：${scopeLabel.value}${audited.value ? ' · 会话可审计' : ''} · ${backendStatus.value}`
      }
    ]
    for (const row of rows) {
      if (row.role !== 'user' && row.role !== 'assistant') continue
      if (row.role === 'user') {
        const { text, attachments: restoredAtts } = splitStoredUserContent(
          row.content || '',
          baseUrl
        )
        const prev = restored[restored.length - 1]
        const attsAlreadyShown =
          prev?.role === 'user' &&
          !prev.content &&
          sameAttachmentNames(prev.attachments, restoredAtts)
        // Keep attachments and text as separate bubbles; skip duplicate att stubs from history.
        if (restoredAtts.length && !attsAlreadyShown) {
          restored.push({
            id: `${row.id}-att`,
            role: 'user',
            content: '',
            attachments: restoredAtts
          })
        }
        if (text) {
          restored.push({
            id: row.id,
            role: 'user',
            content: text
          })
        } else if (!restoredAtts.length) {
          restored.push({
            id: row.id,
            role: 'user',
            content: row.content
          })
        }
        continue
      }
      restored.push({
        id: row.id,
        role: row.role,
        content: row.content,
        reasoning: row.reasoning_content || undefined,
        citations: row.citations || undefined,
        model: row.model || undefined
      })
    }
    if (restored.length === 1) {
      restored.push({
        id: 'a-empty',
        role: 'assistant',
        content: '这是一个空对话，直接提问即可继续。'
      })
    }
    messages.value = restored
    await scrollBottom()
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err)
    showToast(`加载对话失败：${msg}`)
  } finally {
    loadingConversation.value = false
  }
}

function enterMessageShareMode(preselectId?: string): void {
  moreOpenId.value = null
  shareBarMoreOpen.value = false
  const shareable = shareableMessages.value
  if (!shareable.length) {
    showToast('当前对话暂无可分享内容')
    return
  }
  shareSelectMode.value = true
  if (preselectId && shareable.some((m) => m.id === preselectId)) {
    selectedShareIds.value = [preselectId]
  } else {
    selectedShareIds.value = shareable.map((m) => m.id)
  }
}

function exitShareSelectMode(): void {
  shareSelectMode.value = false
  selectedShareIds.value = []
  shareBarMoreOpen.value = false
  shareLinkModalOpen.value = false
  shareLinkCreating.value = false
}

function openShareLinkModal(): void {
  shareBarMoreOpen.value = false
  if (!selectedShareIds.value.length) {
    showToast('请先选择要分享的内容')
    return
  }
  shareLinkModalOpen.value = true
}

function closeShareLinkModal(): void {
  if (shareLinkCreating.value) return
  shareLinkModalOpen.value = false
}

function buildShareSnapshot(): {
  token: string
  sessionId: string | null
  title: string
  messages: Array<{ id: string; role: string; content: string }>
  createdAt: string
} {
  const order = new Map(messages.value.map((m, i) => [m.id, i]))
  const selected = [...selectedShareIds.value].sort(
    (a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0)
  )
  const snapped = selected
    .map((id) => messages.value.find((m) => m.id === id))
    .filter((m): m is Message => !!m)
    .map((m) => ({
      id: m.id,
      role: m.role,
      content: formatMessageForShare(m)
    }))
    .filter((m) => m.content)
  const token = `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
  return {
    token,
    sessionId: sessionId.value,
    title: conversationTitle.value || title.value,
    messages: snapped,
    createdAt: new Date().toISOString()
  }
}

function persistShareSnapshot(snapshot: ReturnType<typeof buildShareSnapshot>): void {
  const key = 'seeker.share.snapshots'
  try {
    const raw = localStorage.getItem(key)
    const map = raw ? (JSON.parse(raw) as Record<string, unknown>) : {}
    map[snapshot.token] = snapshot
    localStorage.setItem(key, JSON.stringify(map))
  } catch {
    // ignore quota / private mode
  }
}

function shareLinkForToken(token: string): string {
  const path = `/ai/share/${encodeURIComponent(token)}`
  const origin = window.location.origin
  const base = window.location.pathname || '/'
  if (origin && origin !== 'null' && !origin.startsWith('file:')) {
    return `${origin}${base}#${path}`
  }
  return `#${path}`
}

async function createAndCopyShareLink(): Promise<void> {
  if (!selectedShareIds.value.length) {
    showToast('请先选择要分享的内容')
    return
  }
  shareLinkCreating.value = true
  try {
    const snapshot = buildShareSnapshot()
    if (!snapshot.messages.length) {
      showToast('所选消息无可分享文本')
      return
    }
    persistShareSnapshot(snapshot)
    const link = shareLinkForToken(snapshot.token)
    await navigator.clipboard.writeText(link)
    showToast('分享链接已创建并复制', 2600)
    shareLinkModalOpen.value = false
    exitShareSelectMode()
  } catch {
    showToast('创建或复制链接失败')
  } finally {
    shareLinkCreating.value = false
  }
}

function isShareSelected(id: string): boolean {
  return selectedShareIds.value.includes(id)
}

function toggleShareSelect(id: string): void {
  if (!shareableMessages.value.some((m) => m.id === id)) return
  if (selectedShareIds.value.includes(id)) {
    selectedShareIds.value = selectedShareIds.value.filter((x) => x !== id)
  } else {
    selectedShareIds.value = [...selectedShareIds.value, id]
  }
}

function toggleShareSelectAll(): void {
  if (shareAllSelected.value) {
    selectedShareIds.value = []
  } else {
    selectedShareIds.value = shareableMessages.value.map((m) => m.id)
  }
}

function onShareableMessageClick(msg: Message): void {
  if (!shareSelectMode.value) return
  toggleShareSelect(msg.id)
}

function formatMessageForShare(msg: Message): string {
  const text = (msg.content || '').trim()
  if (text) return text
  if (msg.attachments?.length) {
    return msg.attachments.map((a) => `【附件】${a.name}`).join('\n')
  }
  return ''
}

watch(
  activeSessionId,
  async (sid) => {
    if (!bootstrapped) return
    if (sid && sid !== sessionId.value) {
      await openConversation(sid)
      if (shareIntentSessionId.value === sid) {
        clearShareIntent()
        enterMessageShareMode()
      }
    }
  }
)

async function handleNavNewChat(): Promise<void> {
  if (!bootstrapped) return
  const key = chatResetKey.value
  if (key === lastHandledResetKey) return
  lastHandledResetKey = key
  consumePendingNewChat()
  await startNewChat()
}

const unregisterNewChat = registerNewChatHandler(() => handleNavNewChat())
onBeforeUnmount(() => {
  unregisterNewChat()
})

/** Fallback when handler was null (unmounted / HMR); deduped with handleNavNewChat. */
watch(chatResetKey, () => {
  void handleNavNewChat()
})

onActivated(() => {
  if (!bootstrapped) return
  if (consumePendingNewChat()) {
    lastHandledResetKey = chatResetKey.value - 1
    void handleNavNewChat()
  }
})

watch(
  () => route.query.session,
  async (sessionQuery) => {
    if (!bootstrapped) {
      bootstrapped = true
      seedMessages()
      await refreshBackendStatus()
      await refreshConversations()
      const sid =
        (typeof sessionQuery === 'string' ? sessionQuery : null) || activeSessionId.value
      if (sid) {
        lastHandledResetKey = chatResetKey.value
        consumePendingNewChat()
        await openConversation(sid)
        if (shareIntentSessionId.value === sid) {
          clearShareIntent()
          enterMessageShareMode()
        }
      } else if (consumePendingNewChat()) {
        lastHandledResetKey = chatResetKey.value - 1
        await handleNavNewChat()
      } else {
        lastHandledResetKey = chatResetKey.value
      }
      // Drop legacy ?session= so keep-alive / tags stay on a stable /ai/chat path.
      if (typeof sessionQuery === 'string') {
        void router.replace({ name: 'AiChat', query: {} })
      }
      return
    }
    const sid = typeof sessionQuery === 'string' ? sessionQuery : null
    if (sid && sid !== sessionId.value) {
      await openConversation(sid)
      void router.replace({ name: 'AiChat', query: {} })
    }
  },
  { immediate: true }
)

watch(conversations, (list) => {
  if (!sessionId.value) return
  const meta = list.find((c) => c.id === sessionId.value)
  if (meta?.title) conversationTitle.value = meta.title
})

async function scrollBottom(): Promise<void> {
  await nextTick()
  if (listRef.value) {
    listRef.value.scrollTop = listRef.value.scrollHeight
  }
}

function scheduleScroll(): void {
  if (scrollRaf) return
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = 0
    void scrollBottom()
  })
}

function historyForApi(): Array<{ role: string; content: string }> {
  const rows: Array<{ role: string; content: string }> = []
  const list = messages.value.filter(
    (m) => (m.role === 'user' || m.role === 'assistant') && !m.streaming
  )
  let i = 0
  while (i < list.length) {
    const m = list[i]
    if (m.role === 'assistant') {
      const text = m.content.trim()
      if (text) rows.push({ role: 'assistant', content: text })
      i += 1
      continue
    }
    // Merge consecutive user bubbles from a split image+text turn into one history item.
    const turn: Message[] = []
    while (i < list.length && list[i].role === 'user') {
      turn.push(list[i])
      i += 1
    }
    const content = questionFromUserTurn(turn).trim()
    if (content) rows.push({ role: 'user', content })
  }
  return rows.slice(-8)
}

function findMessageIndex(id: string): number {
  return messages.value.findIndex((m) => m.id === id)
}

function patchMessage(id: string, patch: Partial<Message>): void {
  const index = findMessageIndex(id)
  if (index < 0) return
  messages.value[index] = { ...messages.value[index], ...patch }
}

function appendContent(id: string, delta: string): void {
  const index = findMessageIndex(id)
  if (index < 0) return
  const current = messages.value[index]
  messages.value[index] = { ...current, content: current.content + delta }
}

function appendReasoning(id: string, delta: string): void {
  const index = findMessageIndex(id)
  if (index < 0) return
  const current = messages.value[index]
  messages.value[index] = {
    ...current,
    reasoning: (current.reasoning || '') + delta
  }
}

function cleanupAskSubscription(): void {
  if (unsubscribeAsk) {
    unsubscribeAsk()
    unsubscribeAsk = null
  }
}

async function stopStreaming(): Promise<void> {
  if (extractAbort) {
    extractAbortKind = 'cancel'
    extractAbort.abort()
    return
  }
  const requestId = activeRequestId.value
  if (!requestId) return
  try {
    await client.askAbort(requestId)
  } catch {
    // ignore abort errors
  }
}

onBeforeUnmount(() => {
  if (extractAbort) {
    extractAbortKind = 'cancel'
    extractAbort.abort()
    extractAbort = null
  }
  void stopStreaming()
  cleanupAskSubscription()
  previewAttachment.value = null
  clearAttachments()
  revokeMessagePreviewUrls(messages.value)
  void stopVoice(false, true)
  listRef.value?.removeEventListener('scroll', onMessagesScroll)
  if (scrollRaf) cancelAnimationFrame(scrollRaf)
  if (copiedTimer) window.clearTimeout(copiedTimer)
  if (toastTimer) window.clearTimeout(toastTimer)
})

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function fileExt(name: string): string {
  const idx = name.lastIndexOf('.')
  if (idx < 0 || idx === name.length - 1) return ''
  return name.slice(idx + 1).toUpperCase()
}

function isPdfAttachment(att: MessageAttachment): boolean {
  return (
    att.mime === 'application/pdf' ||
    att.name.toLowerCase().endsWith('.pdf')
  )
}

function fileTypeLabel(att: MessageAttachment): string {
  if (att.kind === 'image') return '图片'
  if (att.kind === 'audio') return '音频'
  return fileExt(att.name) || '文件'
}

function openAttachmentPreview(att: MessageAttachment): void {
  previewAttachment.value = att
  resetImageStudioView()
  imageStudioMoreOpen.value = false
}

function closeAttachmentPreview(): void {
  previewAttachment.value = null
  resetImageStudioView()
  imageStudioMoreOpen.value = false
}

function resetImageStudioView(): void {
  imageZoom.value = 1
  imagePanX.value = 0
  imagePanY.value = 0
  imagePanning.value = false
}

function zoomImage(delta: number): void {
  const next = Math.min(4, Math.max(0.5, Number((imageZoom.value + delta).toFixed(2))))
  imageZoom.value = next
  if (next <= 1) {
    imagePanX.value = 0
    imagePanY.value = 0
  }
}

function onImageStudioWheel(event: WheelEvent): void {
  if (!isImagePreview.value) return
  event.preventDefault()
  zoomImage(event.deltaY > 0 ? -0.1 : 0.1)
}

function onImageStudioPointerDown(event: PointerEvent): void {
  if (!isImagePreview.value || imageZoom.value <= 1) return
  imagePanning.value = true
  imagePanStartX = event.clientX
  imagePanStartY = event.clientY
  imagePanOriginX = imagePanX.value
  imagePanOriginY = imagePanY.value
  ;(event.currentTarget as HTMLElement | null)?.setPointerCapture?.(event.pointerId)
}

function onImageStudioPointerMove(event: PointerEvent): void {
  if (!imagePanning.value) return
  imagePanX.value = imagePanOriginX + (event.clientX - imagePanStartX)
  imagePanY.value = imagePanOriginY + (event.clientY - imagePanStartY)
}

function onImageStudioPointerUp(event: PointerEvent): void {
  if (!imagePanning.value) return
  imagePanning.value = false
  try {
    ;(event.currentTarget as HTMLElement | null)?.releasePointerCapture?.(event.pointerId)
  } catch {
    // ignore
  }
}

function stubImageStudioTool(label: string): void {
  imageStudioMoreOpen.value = false
  showToast(`「${label}」即将支持`, 2200)
}

async function savePreviewImage(): Promise<void> {
  const att = previewAttachment.value
  if (!att?.previewUrl) {
    showToast('无可保存的图片')
    return
  }
  try {
    const a = document.createElement('a')
    a.href = att.previewUrl
    a.download = att.name || 'image.png'
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    showToast('已开始保存图片')
  } catch {
    showToast('保存失败')
  }
}

async function resolveAttachmentUrl(att: MessageAttachment): Promise<string> {
  if (att.previewUrl) return att.previewUrl
  if (!att.objectKey) throw new Error('无法读取该附件内容')
  const base = await client.getBaseUrl()
  return contentUrlForObjectKey(base, att.objectKey)
}

async function resolveAttachmentFile(att: MessageAttachment): Promise<File> {
  const pending = attachments.value.find((a) => a.id === att.id)
  if (pending?.file) return pending.file
  const url = await resolveAttachmentUrl(att)
  const headers = url.startsWith('blob:') ? undefined : userIdHeaders()
  const resp = await fetch(url, { headers })
  if (!resp.ok) throw new Error('读取附件失败')
  const blob = await resp.blob()
  return new File([blob], att.name, {
    type: att.mime || blob.type || 'application/octet-stream'
  })
}

async function copyImageToClipboard(att: MessageAttachment): Promise<void> {
  const file = await resolveAttachmentFile(att)
  let blob: Blob = file
  if (blob.type !== 'image/png') {
    const bitmap = await createImageBitmap(file)
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('无法处理图片')
    ctx.drawImage(bitmap, 0, 0)
    bitmap.close()
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!png) throw new Error('无法编码图片')
    blob = png
  }
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
}

async function copyAttachmentsAsFiles(atts: MessageAttachment[]): Promise<void> {
  const files = await Promise.all(
    atts.map(async (att) => {
      const file = await resolveAttachmentFile(att)
      const data = new Uint8Array(await file.arrayBuffer())
      return { name: att.name, data }
    })
  )
  const writeFiles = host.clipboardWriteFiles
  if (!writeFiles) throw new Error('当前环境不支持复制文件到剪贴板')
  const result = await writeFiles(files)
  if (!result.ok) throw new Error(result.message || '复制文件失败')
}

function markMessageCopied(id: string): void {
  copiedId.value = id
  if (copiedTimer) window.clearTimeout(copiedTimer)
  copiedTimer = window.setTimeout(() => {
    if (copiedId.value === id) copiedId.value = null
  }, 1600)
}

async function extractPreviewText(target?: MessageAttachment): Promise<void> {
  const att = target || previewAttachment.value
  if (!att || extractingPreviewText.value || composerBusy.value) return
  if (!isExtractableAttachment(att)) {
    showToast('当前附件类型不支持提取文字')
    return
  }
  if (att.kind === 'image' && previewAttachment.value?.id !== att.id) {
    openAttachmentPreview(att)
  }
  extractingPreviewText.value = true
  showToast('正在提取文字…', 2200)
  try {
    const file = await resolveAttachmentFile(att)
    const base = await client.getBaseUrl()
    const form = new FormData()
    form.append('file', file, att.name)
    const controller = new AbortController()
    extractAbort = controller
    extractAbortKind = null
    const timeoutId = window.setTimeout(() => {
      extractAbortKind = 'timeout'
      controller.abort()
    }, EXTRACT_TIMEOUT_MS)
    try {
      const resp = await fetch(`${base}/v1/files/extract-text`, {
        method: 'POST',
        body: form,
        signal: controller.signal
      })
      if (!resp.ok) {
        const detail = await resp.text().catch(() => '')
        throw new Error(detail.slice(0, 240) || resp.statusText)
      }
      const data = (await resp.json()) as { title?: string; text?: string }
      const body = (data.text || '').trim()
      if (!body) throw new Error('未识别到文字')
      const block = `【提取文字：${data.title || att.name}】\n${body}`
      input.value = input.value.trim() ? `${input.value.trim()}\n\n${block}` : block
      focusComposer()
      showToast('已提取文字到输入框', 2600)
    } finally {
      window.clearTimeout(timeoutId)
      extractAbort = null
      extractAbortKind = null
    }
  } catch (err) {
    if (isAbortError(err)) {
      showToast(messageForExtractAbort(), 4000)
    } else {
      const message = err instanceof Error ? err.message : String(err)
      showToast(`提取文字失败：${message}`, 4000)
    }
  } finally {
    extractingPreviewText.value = false
  }
}

function clearAttachments(): void {
  for (const item of attachments.value) {
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
  }
  attachments.value = []
}

function removeAttachment(id: string): void {
  const index = attachments.value.findIndex((a) => a.id === id)
  if (index < 0) return
  const [item] = attachments.value.splice(index, 1)
  if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl)
}

function detectKind(file: File, force?: 'file' | 'image' | 'audio'): ComposerAttachment['kind'] {
  if (force) return force
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('audio/')) return 'audio'
  return 'file'
}

function shouldCreatePreviewUrl(file: File, kind: ComposerAttachment['kind']): boolean {
  if (kind === 'image' || kind === 'audio') return true
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
}

function addFiles(fileList: FileList | File[], force?: 'file' | 'image' | 'audio'): void {
  const files = Array.from(fileList)
  if (!files.length) return
  let added = 0
  for (const file of files) {
    if (attachments.value.length >= MAX_ATTACHMENTS) {
      showToast(`最多添加 ${MAX_ATTACHMENTS} 个附件`)
      break
    }
    if (file.size > MAX_FILE_BYTES) {
      showToast(`${file.name} 超过 20MB`)
      continue
    }
    const kind = detectKind(file, force)
    if (force === 'image' && !file.type.startsWith('image/')) {
      showToast('请选择图片文件')
      continue
    }
    const previewUrl = shouldCreatePreviewUrl(file, kind)
      ? URL.createObjectURL(file)
      : undefined
    attachments.value.push({
      id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      kind,
      name: file.name,
      size: file.size,
      mime: file.type || 'application/octet-stream',
      previewUrl,
      file
    })
    added += 1
  }
  if (added) {
    plusMenuOpen.value = false
    focusComposer()
  }
}

function openFilePicker(): void {
  plusMenuOpen.value = false
  fileInputRef.value?.click()
}

function openImagePicker(): void {
  plusMenuOpen.value = false
  imageInputRef.value?.click()
}

function onFileInputChange(event: Event): void {
  const target = event.target as HTMLInputElement
  if (target.files?.length) addFiles(target.files, 'file')
  target.value = ''
}

function onImageInputChange(event: Event): void {
  const target = event.target as HTMLInputElement
  if (target.files?.length) addFiles(target.files, 'image')
  target.value = ''
}

function onComposerPaste(event: ClipboardEvent): void {
  const items = event.clipboardData?.items
  if (!items) return
  const files: File[] = []
  for (const item of Array.from(items)) {
    if (item.kind === 'file') {
      const file = item.getAsFile()
      if (file) files.push(file)
    }
  }
  if (!files.length) return
  event.preventDefault()
  addFiles(files)
}

function attachmentNote(list: MessageAttachment[]): string {
  if (!list.length) return ''
  const parts = list.map((a) => {
    const label = a.kind === 'image' ? '图片' : a.kind === 'audio' ? '语音' : '附件'
    const keySuffix = a.objectKey ? `|key:${a.objectKey}` : ''
    return `${label} ${a.name}${keySuffix}`
  })
  return `【已附带：${parts.join('；')}】`
}

function contentUrlForObjectKey(baseUrl: string, objectKey: string): string {
  const base = baseUrl.replace(/\/$/, '')
  return `${base}/v1/files/content?key=${encodeURIComponent(objectKey)}`
}

function userIdHeaders(): HeadersInit {
  const id = externalUserId.value
  if (!id) return {}
  return { 'X-User-Id': encodeURIComponent(id) }
}

function inferAttachmentKind(
  label: string,
  name: string
): MessageAttachment['kind'] {
  if (label === '图片' || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name)) return 'image'
  if (label === '语音' || /\.(webm|mp3|wav|m4a|ogg|aac)$/i.test(name)) return 'audio'
  return 'file'
}

function inferAttachmentMime(kind: MessageAttachment['kind'], name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() || ''
  if (kind === 'image') {
    if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg'
    if (ext === 'png') return 'image/png'
    if (ext === 'gif') return 'image/gif'
    if (ext === 'webp') return 'image/webp'
    return 'image/*'
  }
  if (kind === 'audio') {
    if (ext === 'webm') return 'audio/webm'
    if (ext === 'mp3') return 'audio/mpeg'
    if (ext === 'wav') return 'audio/wav'
    if (ext === 'm4a') return 'audio/mp4'
    return 'audio/*'
  }
  if (ext === 'pdf') return 'application/pdf'
  return 'application/octet-stream'
}

function parseAttachmentNoteBody(
  body: string,
  baseUrl?: string
): MessageAttachment[] {
  const parts = body
    .split('；')
    .map((part) => part.trim())
    .filter(Boolean)
  const stamp = Date.now()
  return parts
    .map((part, index) => {
      const matched = /^(附件|图片|语音)\s+(.+?)(?:\|key:([^\s；]+))?$/.exec(part)
      const label = matched?.[1] || '附件'
      const name = (matched?.[2] || part).trim()
      const objectKey = matched?.[3]?.trim() || undefined
      const kind = inferAttachmentKind(label, name)
      const previewUrl =
        objectKey && baseUrl ? contentUrlForObjectKey(baseUrl, objectKey) : undefined
      return {
        id: `restored-att-${stamp}-${index}`,
        kind,
        name,
        size: 0,
        mime: inferAttachmentMime(kind, name),
        objectKey,
        previewUrl
      }
    })
    .filter((att) => !!att.name)
}

/** Restore UI attachments from stored ask text that appended 【已附带：…】. */
function splitStoredUserContent(
  content: string,
  baseUrl?: string
): {
  text: string
  attachments: MessageAttachment[]
} {
  const attachments: MessageAttachment[] = []
  let match: RegExpExecArray | null
  const re = new RegExp(ATTACHMENT_NOTE_RE.source, 'g')
  while ((match = re.exec(content)) !== null) {
    attachments.push(...parseAttachmentNoteBody(match[1] || '', baseUrl))
  }
  let text = content
    .replace(/【已附带：[^】]*】/g, '')
    // OCR bodies are model context only; never show them in the bubble.
    .replace(/【附件正文：[\s\S]*$/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  if (text === DEFAULT_ATTACH_QUESTION) text = ''
  if (!attachments.length) {
    const hadMeta = /【(?:已附带|附件正文)：/.test(content)
    return { text: hadMeta ? text : content, attachments: [] }
  }
  return { text, attachments }
}

function sameAttachmentNames(
  a: MessageAttachment[] | undefined,
  b: MessageAttachment[] | undefined
): boolean {
  if (!a?.length || !b?.length || a.length !== b.length) return false
  return a.every((att, i) => att.kind === b[i].kind && att.name === b[i].name)
}

function buildQuestion(text: string, list: MessageAttachment[]): string {
  const note = attachmentNote(list)
  if (text && note) return `${text}\n\n${note}`
  if (text) return text
  return `${DEFAULT_ATTACH_QUESTION}\n\n${note}`
}

function isExtractableAttachment(att: ComposerAttachment | MessageAttachment): boolean {
  const name = att.name.toLowerCase()
  if (att.kind === 'audio') return false
  if (att.kind === 'image') return true
  return (
    att.mime === 'application/pdf' ||
    att.mime.startsWith('text/') ||
    att.mime.startsWith('image/') ||
    name.endsWith('.pdf') ||
    name.endsWith('.png') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.webp') ||
    name.endsWith('.bmp') ||
    name.endsWith('.tif') ||
    name.endsWith('.tiff') ||
    name.endsWith('.txt') ||
    name.endsWith('.md') ||
    name.endsWith('.csv') ||
    name.endsWith('.json') ||
    name.endsWith('.log')
  )
}

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  )
}

function messageForExtractAbort(): string {
  if (extractAbortKind === 'timeout') {
    return `解析附件超时（${EXTRACT_TIMEOUT_MS / 1000}s），请稍后重试或减小文件`
  }
  return '已取消解析附件'
}

async function uploadChatAttachments(
  pending: ComposerAttachment[],
  signal?: AbortSignal
): Promise<Map<string, string>> {
  const uploaded = new Map<string, string>()
  if (!pending.length) return uploaded
  const base = await client.getBaseUrl()
  for (const att of pending) {
    if (signal?.aborted) {
      throw new Error('已取消上传附件')
    }
    const form = new FormData()
    form.append('file', att.file, att.name)
    if (sessionId.value) form.append('session_id', sessionId.value)
    const resp = await fetch(`${base}/v1/files/upload`, {
      method: 'POST',
      body: form,
      headers: userIdHeaders(),
      signal
    })
    if (!resp.ok) {
      const detail = await resp.text().catch(() => '')
      throw new Error(
        `上传附件失败「${att.name}」: ${detail.slice(0, 240) || resp.statusText}`
      )
    }
    const data = (await resp.json()) as { object_key?: string }
    if (!data.object_key) {
      throw new Error(`上传附件失败「${att.name}」: 未返回 object_key`)
    }
    uploaded.set(att.id, data.object_key)
  }
  return uploaded
}

async function extractAttachmentBodies(
  pending: ComposerAttachment[],
  signal?: AbortSignal
): Promise<string[]> {
  const extractable = pending.filter(isExtractableAttachment)
  if (!extractable.length) return []
  const base = await client.getBaseUrl()
  const parts: string[] = []
  for (const att of extractable) {
    if (signal?.aborted) {
      throw new Error(messageForExtractAbort())
    }
    const form = new FormData()
    form.append('file', att.file, att.name)
    try {
      const resp = await fetch(`${base}/v1/files/extract-text`, {
        method: 'POST',
        body: form,
        headers: userIdHeaders(),
        signal
      })
      if (!resp.ok) {
        const detail = await resp.text().catch(() => '')
        throw new Error(
          `解析附件失败「${att.name}」: ${detail.slice(0, 240) || resp.statusText}`
        )
      }
      const data = (await resp.json()) as { title?: string; text?: string }
      const body = (data.text || '').trim()
      if (body) {
        parts.push(`【附件正文：${data.title || att.name}】\n${body}`)
      }
    } catch (err) {
      if (isAbortError(err) || signal?.aborted) {
        throw new Error(messageForExtractAbort())
      }
      throw err
    }
  }
  return parts
}

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  const w = window as Window & {
    SpeechRecognition?: new () => SpeechRecognitionLike
    webkitSpeechRecognition?: new () => SpeechRecognitionLike
  }
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

async function stopVoice(showHint = true, discardRecording = false): Promise<void> {
  const wasActive = voiceMode.value !== 'idle'
  if (recognition) {
    try {
      recognition.onresult = null
      recognition.onerror = null
      recognition.onend = null
      recognition.abort()
    } catch {
      // ignore
    }
    recognition = null
  }
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    if (discardRecording) {
      mediaRecorder.ondataavailable = null
      mediaRecorder.onstop = () => {
        audioChunks = []
        if (mediaStream) {
          for (const track of mediaStream.getTracks()) track.stop()
          mediaStream = null
        }
        mediaRecorder = null
        voiceMode.value = 'idle'
      }
    }
    mediaRecorder.stop()
    if (showHint && wasActive) showToast('已结束语音')
    return
  }
  if (mediaStream) {
    for (const track of mediaStream.getTracks()) track.stop()
    mediaStream = null
  }
  audioChunks = []
  mediaRecorder = null
  if (wasActive && showHint) showToast('已结束语音')
  voiceMode.value = 'idle'
}

async function startSpeechDictation(): Promise<boolean> {
  const Ctor = getSpeechRecognitionCtor()
  if (!Ctor) return false
  const instance = new Ctor()
  instance.lang = 'zh-CN'
  instance.continuous = true
  instance.interimResults = true
  voiceBaseText = input.value.replace(/\u200b/g, '').trimEnd()
  voiceFinalText = ''
  instance.onresult = (event) => {
    let interim = ''
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const result = event.results[i]
      const piece = result[0]?.transcript || ''
      if (result.isFinal) voiceFinalText += piece
      else interim += piece
    }
    const spoken = `${voiceFinalText}${interim}`.trim()
    input.value = [voiceBaseText, spoken].filter(Boolean).join(voiceBaseText && spoken ? ' ' : '')
  }
  instance.onerror = () => {
    void stopVoice(false)
    showToast('语音识别中断')
  }
  instance.onend = () => {
    if (voiceMode.value === 'listening') {
      voiceMode.value = 'idle'
      recognition = null
    }
  }
  recognition = instance
  try {
    instance.start()
  } catch {
    recognition = null
    return false
  }
  voiceMode.value = 'listening'
  showToast('正在听写，再次点击麦克风结束')
  return true
}

async function startAudioRecording(): Promise<void> {
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true })
  } catch {
    showToast('无法访问麦克风')
    return
  }
  audioChunks = []
  const recorder = new MediaRecorder(mediaStream)
  mediaRecorder = recorder
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) audioChunks.push(event.data)
  }
  recorder.onstop = () => {
    const blob = new Blob(audioChunks, { type: recorder.mimeType || 'audio/webm' })
    audioChunks = []
    if (blob.size > 0) {
      const file = new File([blob], `语音-${new Date().toISOString().slice(11, 19).replace(/:/g, '')}.webm`, {
        type: blob.type
      })
      addFiles([file], 'audio')
      showToast('已添加语音附件')
    }
    if (mediaStream) {
      for (const track of mediaStream.getTracks()) track.stop()
      mediaStream = null
    }
    mediaRecorder = null
    voiceMode.value = 'idle'
  }
  recorder.start()
  voiceMode.value = 'recording'
  showToast('正在录音，再次点击麦克风结束')
}

async function toggleVoice(): Promise<void> {
  if (composerBusy.value) return
  if (voiceMode.value !== 'idle') {
    await stopVoice(true)
    return
  }
  const usedSpeech = await startSpeechDictation()
  if (!usedSpeech) await startAudioRecording()
}

function closePlusMenu(): void {
  plusMenuOpen.value = false
}

function showToast(text: string, durationMs = 1800): void {
  actionToast.value = text
  if (toastTimer) window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => {
    actionToast.value = ''
  }, durationMs)
}

async function copyMessage(msg: Message): Promise<void> {
  const text = msg.content.trim()
  const atts = msg.attachments || []

  if (text) {
    try {
      await navigator.clipboard.writeText(text)
      markMessageCopied(msg.id)
      showToast('已复制')
    } catch {
      showToast('复制失败')
    }
    return
  }

  if (!atts.length) {
    showToast('无可复制内容')
    return
  }

  try {
    if (atts.length === 1 && atts[0].kind === 'image') {
      try {
        await copyImageToClipboard(atts[0])
        markMessageCopied(msg.id)
        showToast('已复制图片')
        return
      } catch {
        // Fall through to file-drop clipboard for desktop paste.
      }
    }
    await copyAttachmentsAsFiles(atts)
    markMessageCopied(msg.id)
    showToast(atts.length > 1 ? `已复制 ${atts.length} 个文件` : '已复制文件')
  } catch {
    showToast('复制失败')
  }
}

async function shareMessage(msg: Message): Promise<void> {
  moreOpenId.value = null
  enterMessageShareMode(msg.id)
}

function focusComposer(): void {
  void nextTick(() => {
    const el = composerRef.value
    if (!el) return
    el.focus()
    const len = el.value.length
    el.setSelectionRange(len, len)
  })
}

function startEdit(msg: Message): void {
  if (composerBusy.value) return
  if (!msg.content.trim()) {
    showToast('附件消息不支持直接修改，可删除后重新发送')
    return
  }
  moreOpenId.value = null
  editingId.value = msg.id
  input.value = msg.content
  focusComposer()
  showToast('正在修改，发送后将替换此条及之后的回复')
}

function cancelEdit(): void {
  editingId.value = null
  input.value = ''
}

function quoteMessage(msg: Message): void {
  moreOpenId.value = null
  if (!msg.content.trim()) {
    showToast('附件消息无法引用正文')
    return
  }
  const quote = msg.content
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n')
  input.value = input.value.trim() ? `${input.value.trim()}\n\n${quote}\n\n` : `${quote}\n\n`
  focusComposer()
  showToast('已引用到输入框')
}

function deleteMessage(msg: Message): void {
  if (composerBusy.value) return
  moreOpenId.value = null
  if (editingId.value === msg.id) cancelEdit()
  if (previewAttachment.value && msg.attachments?.some((a) => a.id === previewAttachment.value?.id)) {
    previewAttachment.value = null
  }
  for (const att of msg.attachments || []) {
    if (att.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(att.previewUrl)
  }
  messages.value = messages.value.filter((m) => m.id !== msg.id)
  showToast('已删除')
}

function toggleMore(id: string): void {
  const next = moreOpenId.value === id ? null : id
  moreOpenId.value = next
  if (!next) return
  void nextTick(() => {
    const wrap = document.querySelector(`.more-wrap[data-more-id="${CSS.escape(next)}"]`) as HTMLElement | null
    wrap?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  })
}

function closeMore(): void {
  moreOpenId.value = null
  shareBarMoreOpen.value = false
  imageStudioMoreOpen.value = false
}

const DEFAULT_SUGGESTIONS = ['举一个具体例子', '整理成可执行清单', '再解释一下边界情况']

function clearAllSuggestions(): void {
  messages.value = messages.value.map((m) =>
    m.suggestions || m.suggestionsLoading
      ? { ...m, suggestions: undefined, suggestionsLoading: false }
      : m
  )
}

function fallbackSuggestions(): string[] {
  return (scene.value?.suggestions?.length ? scene.value.suggestions : DEFAULT_SUGGESTIONS).slice(
    0,
    3
  )
}

async function loadSuggestions(assistantId: string, question: string, answer: string): Promise<void> {
  if (!answer.trim() || messages.value[findMessageIndex(assistantId)]?.error) return
  patchMessage(assistantId, { suggestionsLoading: true, suggestions: undefined })
  try {
    const resp = await client.suggest({
      question,
      answer,
      scene: scene.value?.title || title.value,
      count: 3
    })
    const list = resp.suggestions?.length ? resp.suggestions : fallbackSuggestions()
    // Ignore if a newer turn already replaced this message state
    const current = messages.value[findMessageIndex(assistantId)]
    if (!current || current.streaming || current.error) return
    patchMessage(assistantId, { suggestions: list, suggestionsLoading: false })
    scheduleScroll()
  } catch {
    const current = messages.value[findMessageIndex(assistantId)]
    if (!current || current.streaming || current.error) return
    patchMessage(assistantId, { suggestions: fallbackSuggestions(), suggestionsLoading: false })
    scheduleScroll()
  }
}

async function applySuggestion(text: string): Promise<void> {
  if (composerBusy.value || !text.trim()) return
  input.value = text.trim()
  await send()
}

function revokeRemovedAttachments(
  removed: Message[],
  keepPreviewUrls: Set<string> = new Set()
): void {
  for (const msg of removed) {
    for (const att of msg.attachments || []) {
      if (att.previewUrl?.startsWith('blob:') && !keepPreviewUrls.has(att.previewUrl)) {
        URL.revokeObjectURL(att.previewUrl)
      }
    }
  }
}

/** Collect consecutive user messages immediately before an assistant reply. */
function precedingUserTurn(assistantIndex: number): Message[] {
  const turn: Message[] = []
  let i = assistantIndex - 1
  while (i >= 0 && messages.value[i].role === 'user') {
    turn.unshift(messages.value[i])
    i -= 1
  }
  return turn
}

function questionFromUserTurn(turn: Message[]): string {
  const text = turn
    .map((m) => m.content.trim())
    .filter(Boolean)
    .join('\n')
  const atts = turn.flatMap((m) => m.attachments || [])
  return buildQuestion(text, atts)
}

async function runAsk(question: string): Promise<void> {
  sending.value = true
  clearAllSuggestions()
  moreOpenId.value = null

  const stamp = Date.now()
  const requestId = `ask-${stamp}-${Math.random().toString(36).slice(2, 8)}`
  const assistantId = `a-${stamp}`
  activeRequestId.value = requestId

  messages.value.push({
    id: assistantId,
    role: 'assistant',
    content: '',
    streaming: true
  })
  await scrollBottom()

  cleanupAskSubscription()
  unsubscribeAsk = client.onAskEvent((event) => {
    if (event.requestId !== requestId) return
    if (event.type === 'reasoning') {
      appendReasoning(assistantId, event.delta)
      scheduleScroll()
    } else if (event.type === 'token') {
      appendContent(assistantId, event.delta)
      scheduleScroll()
    } else if (event.type === 'citations') {
      patchMessage(assistantId, { citations: event.citations })
    } else if (event.type === 'session_id') {
      sessionId.value = event.sessionId
      syncSessionQuery(event.sessionId)
    } else if (event.type === 'model') {
      patchMessage(assistantId, { model: event.model })
    } else if (event.type === 'error') {
      const index = findMessageIndex(assistantId)
      const existing = index >= 0 ? messages.value[index].content : ''
      patchMessage(assistantId, {
        content:
          existing ||
          `请求失败：${event.message}\n\n请确认 seeker-ai 已启动（默认 http://127.0.0.1:8000），可用环境变量 SEEKER_API_BASE 覆盖。`,
        error: !existing,
        streaming: false
      })
    } else if (event.type === 'done') {
      patchMessage(assistantId, { streaming: false })
    }
  })

  try {
    const resp = await client.askStream({
      question,
      sessionId: sessionId.value,
      spaceNames: backend.value.spaceNames,
      aclGroupIds: backend.value.aclGroupIds,
      history: historyForApi().slice(0, -1),
      requestId,
      externalUserId: externalUserId.value
    })
    if (resp.session_id) {
      sessionId.value = resp.session_id
      syncSessionQuery(resp.session_id)
    }
    const finalAnswer =
      resp.answer || messages.value[findMessageIndex(assistantId)]?.content || ''
    const finalReasoning =
      resp.reasoning_content ||
      messages.value[findMessageIndex(assistantId)]?.reasoning ||
      undefined
    patchMessage(assistantId, {
      content: finalAnswer,
      reasoning: finalReasoning || undefined,
      citations: resp.citations?.length
        ? resp.citations
        : messages.value[findMessageIndex(assistantId)]?.citations,
      model: resp.model || messages.value[findMessageIndex(assistantId)]?.model,
      streaming: false
    })
    if (finalAnswer.trim() && !messages.value[findMessageIndex(assistantId)]?.error) {
      void loadSuggestions(assistantId, question, finalAnswer)
    }
    void refreshConversations()
    if (!conversationTitle.value && question.trim()) {
      conversationTitle.value = question.trim().slice(0, 36)
    }
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : String(err)
    const index = findMessageIndex(assistantId)
    const existing = index >= 0 ? messages.value[index].content : ''
    if (existing && (errMsg === '已停止生成' || errMsg === '请求已取消')) {
      patchMessage(assistantId, { streaming: false })
      if (existing.trim()) void loadSuggestions(assistantId, question, existing)
    } else {
      patchMessage(assistantId, {
        content:
          existing ||
          `请求失败：${errMsg}\n\n请确认 seeker-ai 已启动（默认 http://127.0.0.1:8000），可用环境变量 SEEKER_API_BASE 覆盖。`,
        error: !existing,
        streaming: false,
        suggestionsLoading: false,
        suggestions: undefined
      })
    }
  } finally {
    cleanupAskSubscription()
    activeRequestId.value = null
    sending.value = false
    await scrollBottom()
  }
}

async function retryAssistant(msg: Message): Promise<void> {
  if (composerBusy.value || msg.role !== 'assistant') return
  const index = findMessageIndex(msg.id)
  if (index < 0) return

  const turn = precedingUserTurn(index)
  if (!turn.length) {
    showToast('找不到对应的提问，无法重试')
    return
  }

  const question = questionFromUserTurn(turn)
  if (!question.trim()) {
    showToast('无可重试的提问内容')
    return
  }

  moreOpenId.value = null
  await stopVoice(false, true)
  plusMenuOpen.value = false

  const removed = messages.value.slice(index)
  revokeRemovedAttachments(removed)
  messages.value = messages.value.slice(0, index)

  showToast('正在重新生成…')
  await runAsk(question)
}

async function send(): Promise<void> {
  const text = input.value.replace(/\u200b/g, '').trim()
  const pending = attachments.value.slice()
  if ((!text && !pending.length) || composerBusy.value) return

  await stopVoice(false, true)
  plusMenuOpen.value = false

  const displayAttachments: MessageAttachment[] = pending.map(
    ({ id, kind, name, size, mime, previewUrl, objectKey }) => ({
      id,
      kind,
      name,
      size,
      mime,
      previewUrl,
      objectKey
    })
  )
  const stamp = Date.now()
  const keepUrls = new Set(
    displayAttachments.map((a) => a.previewUrl).filter((u): u is string => !!u)
  )

  const editingMessageId = editingId.value
  if (editingMessageId) {
    const index = findMessageIndex(editingMessageId)
    if (index >= 0) {
      revokeRemovedAttachments(messages.value.slice(index), keepUrls)
      messages.value = messages.value.slice(0, index)
    }
    editingId.value = null
  }

  // Attachments and text stay as separate user bubbles (same for files/images).
  if (displayAttachments.length) {
    messages.value.push({
      id: `u-att-${stamp}`,
      role: 'user',
      content: '',
      attachments: displayAttachments
    })
  }
  if (text) {
    messages.value.push({
      id: `u-${stamp}`,
      role: 'user',
      content: text
    })
  }

  input.value = ''
  attachments.value = []
  await scrollBottom()

  const needsUpload = pending.length > 0
  const needsExtract = pending.some(isExtractableAttachment)
  let questionBase = buildQuestion(text, displayAttachments)

  if (needsUpload || needsExtract) {
    extracting.value = true
    sending.value = true
    const statusId = `a-extract-${stamp}`
    messages.value.push({
      id: statusId,
      role: 'assistant',
      content: needsUpload
        ? '正在上传并解析附件，请稍候…\nCPU 解析扫描件可能需要数分钟，请耐心等待。'
        : '正在解析附件，请稍候…\nCPU 解析扫描件可能需要数分钟，请耐心等待。',
      streaming: true
    })
    await scrollBottom()

    const controller = new AbortController()
    extractAbort = controller
    extractAbortKind = null
    const timeoutId = window.setTimeout(() => {
      extractAbortKind = 'timeout'
      controller.abort()
    }, EXTRACT_TIMEOUT_MS)

    try {
      if (needsUpload) {
        const uploaded = await uploadChatAttachments(pending, controller.signal)
        for (const att of displayAttachments) {
          const key = uploaded.get(att.id)
          if (key) att.objectKey = key
        }
        questionBase = buildQuestion(text, displayAttachments)
      }
      let question = questionBase
      if (needsExtract) {
        const bodies = await extractAttachmentBodies(pending, controller.signal)
        if (bodies.length) {
          question = `${questionBase}\n\n${bodies.join('\n\n')}`
        }
      }
      const statusIndex = findMessageIndex(statusId)
      if (statusIndex >= 0) {
        messages.value.splice(statusIndex, 1)
      }
      extracting.value = false
      await runAsk(question)
      return
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      const cancelled =
        extractAbortKind === 'cancel' ||
        message.includes('取消') ||
        isAbortError(err)
      patchMessage(statusId, {
        content: cancelled ? '已取消解析附件。' : message,
        error: !cancelled,
        streaming: false
      })
      showToast(message, 4000)
      sending.value = false
      await scrollBottom()
      return
    } finally {
      window.clearTimeout(timeoutId)
      extractAbort = null
      extractAbortKind = null
      extracting.value = false
    }
  }

  await runAsk(questionBase)
}
</script>

<template>
  <div :class="ns.b()" @click="closeMore(); closePlusMenu()">
    <div
      class="chat-workspace"
      :class="{ 'has-preview': !!previewAttachment, 'image-studio-open': isImagePreview }"
    >
    <section class="chat" :class="{ 'share-select': shareSelectMode }">
    <header class="chat-head">
      <template v-if="shareSelectMode">
        <div class="share-select-head chat-share-head">
          <span class="share-select-title">选择内容</span>
          <button type="button" class="share-cancel-btn" @click="exitShareSelectMode">取消</button>
        </div>
      </template>
      <template v-else>
        <div>
          <h1>{{ title }}</h1>
          <p>{{ subtitle }}</p>
        </div>
        <ScopeBadge :label="scopeLabel" :audited="audited" />
      </template>
    </header>

    <div class="messages-shell">
    <div ref="listRef" class="messages">
      <div
        v-for="msg in messages"
        :key="msg.id"
        class="msg-row"
        :class="[
          msg.role,
          {
            'share-pick': shareSelectMode && (msg.role === 'user' || msg.role === 'assistant'),
            'share-selected': shareSelectMode && isShareSelected(msg.id)
          }
        ]"
        :data-msg-id="msg.role === 'user' ? msg.id : undefined"
        @click="shareSelectMode && (msg.role === 'user' || msg.role === 'assistant') && onShareableMessageClick(msg)"
      >
        <span
          v-if="shareSelectMode && (msg.role === 'user' || msg.role === 'assistant') && !msg.streaming"
          class="share-check msg-share-check"
          :class="{ on: isShareSelected(msg.id) }"
          aria-hidden="true"
        />
        <article
          class="bubble"
          :class="[
            msg.role,
            {
              error: msg.error,
              streaming: msg.streaming,
              editing: editingId === msg.id,
              'attach-only': !!msg.attachments?.length && !msg.content,
              'share-dim': shareSelectMode && (msg.role === 'user' || msg.role === 'assistant') && !isShareSelected(msg.id)
            }
          ]"
        >
          <div v-if="msg.attachments?.length" class="msg-attachments" :class="{ solo: !msg.content }">
            <button
              v-for="att in msg.attachments"
              :key="att.id"
              type="button"
              class="msg-att"
              :class="[
                att.kind,
                { active: previewAttachment?.id === att.id, clickable: true }
              ]"
              :title="`查看 ${att.name}`"
              @click.stop="openAttachmentPreview(att)"
            >
              <img
                v-if="att.kind === 'image' && att.previewUrl"
                :src="att.previewUrl"
                :alt="att.name"
              />
              <div v-else-if="att.kind === 'audio'" class="msg-att-file audio">
                <span class="msg-att-icon" aria-hidden="true">♪</span>
                <span class="msg-att-meta">
                  <span class="msg-att-name">{{ att.name }}</span>
                  <span class="msg-att-type">{{ fileTypeLabel(att) }}</span>
                </span>
              </div>
              <div v-else class="msg-att-file">
                <span class="msg-att-icon" :data-ext="fileExt(att.name) || 'FILE'" aria-hidden="true">
                  {{ fileExt(att.name).slice(0, 3) || 'FILE' }}
                </span>
                <span class="msg-att-meta">
                  <span class="msg-att-name">{{ att.name }}</span>
                  <span class="msg-att-type">{{ fileTypeLabel(att) }}</span>
                </span>
              </div>
            </button>
          </div>
          <details
            v-if="msg.reasoning"
            class="thinking-block"
            :open="msg.streaming && !msg.content"
          >
            <summary>
              <span class="thinking-label">{{
                msg.streaming && !msg.content ? '思考中…' : '思考过程'
              }}</span>
            </summary>
            <div class="thinking-body">
              {{ msg.reasoning
              }}<span
                v-if="msg.streaming && !msg.content"
                class="caret"
                aria-hidden="true"
              />
            </div>
          </details>
          <p v-if="msg.content">
            {{ msg.content }}<span v-if="msg.streaming" class="caret" aria-hidden="true" />
          </p>
          <p v-else-if="msg.streaming && !msg.reasoning">
            <span class="caret" aria-hidden="true" />
          </p>
          <ul v-if="msg.citations?.length" class="cites">
            <li v-for="(c, i) in msg.citations" :key="c.chunk_id || `${c.document_id}-${i}`">
              [{{ i + 1 }}] {{ c.title }}
              <span class="score">{{ c.score.toFixed(2) }}</span>
              <span v-if="c.snippet" class="snip">{{ c.snippet }}</span>
            </li>
          </ul>
        </article>

        <div
          v-if="msg.role === 'assistant' && !msg.streaming && !shareSelectMode"
          class="msg-actions assistant"
          :class="{ open: moreOpenId === msg.id || copiedId === msg.id }"
          @click.stop
        >
          <button
            type="button"
            class="icon-btn"
            :title="copiedId === msg.id ? '已复制' : '复制'"
            :aria-label="copiedId === msg.id ? '已复制' : '复制'"
            @click="copyMessage(msg)"
          >
            <svg v-if="copiedId !== msg.id" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="9" y="9" width="11" height="11" rx="2" fill="none" stroke="currentColor" stroke-width="1.8" />
              <path
                d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M5 12.5 9.5 17 19 7.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <button
            type="button"
            class="icon-btn"
            title="重试"
            aria-label="重试"
            :disabled="sending"
            @click="retryAssistant(msg)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M4.5 12a7.5 7.5 0 0 1 12.7-5.4M19.5 12a7.5 7.5 0 0 1-12.7 5.4"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
              <path
                d="M17 3.8v4.2h-4.2M7 20.2v-4.2h4.2"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <div class="more-wrap" :data-more-id="msg.id">
            <button
              type="button"
              class="icon-btn"
              title="更多"
              aria-label="更多"
              :aria-expanded="moreOpenId === msg.id"
              @click="toggleMore(msg.id)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="6" cy="12" r="1.5" fill="currentColor" />
                <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                <circle cx="18" cy="12" r="1.5" fill="currentColor" />
              </svg>
            </button>
            <div v-if="moreOpenId === msg.id" class="more-menu" role="menu">
              <button type="button" role="menuitem" @click="quoteMessage(msg)">引用到输入框</button>
              <button type="button" role="menuitem" @click="shareMessage(msg)">分享</button>
              <button type="button" role="menuitem" @click="copyMessage(msg)">复制全文</button>
              <button
                type="button"
                role="menuitem"
                class="danger"
                :disabled="sending"
                @click="deleteMessage(msg)"
              >
                删除
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="!shareSelectMode && msg.role === 'assistant' && !msg.streaming && !msg.error && (msg.suggestionsLoading || msg.suggestions?.length)"
          class="followups"
          @click.stop
        >
          <template v-if="msg.suggestionsLoading && !msg.suggestions?.length">
            <span class="followup-skel" /><span class="followup-skel" /><span class="followup-skel" />
          </template>
          <button
            v-for="(tip, tipIdx) in msg.suggestions || []"
            :key="`${msg.id}-s-${tipIdx}`"
            type="button"
            class="followup-chip"
            :disabled="sending"
            @click="applySuggestion(tip)"
          >
            {{ tip }}
            <span aria-hidden="true">→</span>
          </button>
        </div>

        <div
          v-if="msg.role === 'user' && !shareSelectMode"
          class="msg-actions"
          :class="{ open: moreOpenId === msg.id || copiedId === msg.id || editingId === msg.id }"
          @click.stop
        >
          <button
            type="button"
            class="icon-btn"
            :title="copiedId === msg.id ? '已复制' : '复制'"
            :aria-label="copiedId === msg.id ? '已复制' : '复制'"
            @click="copyMessage(msg)"
          >
            <svg v-if="copiedId !== msg.id" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="9" y="9" width="11" height="11" rx="2" fill="none" stroke="currentColor" stroke-width="1.8" />
              <path
                d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M5 12.5 9.5 17 19 7.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <button
            type="button"
            class="icon-btn"
            title="分享"
            aria-label="分享"
            @click="shareMessage(msg)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="18" cy="5" r="2.2" fill="none" stroke="currentColor" stroke-width="1.6" />
              <circle cx="6" cy="12" r="2.2" fill="none" stroke="currentColor" stroke-width="1.6" />
              <circle cx="18" cy="19" r="2.2" fill="none" stroke="currentColor" stroke-width="1.6" />
              <path
                d="M8.1 13.1 15.9 17M15.9 7 8.1 10.9"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </button>

          <button
            type="button"
            class="icon-btn"
            title="修改"
            aria-label="修改"
            :disabled="sending || !msg.content.trim()"
            @click="startEdit(msg)"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M4 20h4.2L19.5 8.7a1.8 1.8 0 0 0 0-2.5L17.8 4.5a1.8 1.8 0 0 0-2.5 0L4 15.8V20z"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linejoin="round"
              />
              <path
                d="M13.5 6.5 17.5 10.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>

          <div class="more-wrap" :data-more-id="msg.id">
            <button
              type="button"
              class="icon-btn"
              title="更多"
              aria-label="更多"
              :aria-expanded="moreOpenId === msg.id"
              @click="toggleMore(msg.id)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="6" cy="12" r="1.5" fill="currentColor" />
                <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                <circle cx="18" cy="12" r="1.5" fill="currentColor" />
              </svg>
            </button>
            <div v-if="moreOpenId === msg.id" class="more-menu" role="menu">
              <button type="button" role="menuitem" @click="quoteMessage(msg)">引用到输入框</button>
              <button type="button" role="menuitem" @click="copyMessage(msg)">复制全文</button>
              <button
                type="button"
                role="menuitem"
                class="danger"
                :disabled="sending"
                @click="deleteMessage(msg)"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <nav
      v-if="showMsgRail && !previewAttachment && !shareSelectMode"
      class="msg-rail"
      :class="{ expanded: railExpanded }"
      aria-label="消息快速定位"
      @mouseenter="openMsgRail"
      @mouseleave="closeMsgRail"
      @focusin="openMsgRail"
      @focusout="onRailFocusOut"
    >
      <div v-if="railExpanded" class="msg-rail-panel">
        <button
          v-for="item in userNavItems"
          :key="item.id"
          type="button"
          class="msg-rail-item"
          :class="{
            active: activeNavId === item.id,
            hover: hoveredNavId === item.id
          }"
          :aria-label="item.summary"
          :aria-current="activeNavId === item.id ? 'true' : undefined"
          @click="jumpToMessage(item.id)"
          @mouseenter="onNavItemEnter(item, $event)"
          @mouseleave="onNavItemLeave"
          @focus="onNavItemEnter(item, $event)"
        >
          <span class="msg-rail-text">{{ item.summary }}</span>
          <span class="msg-rail-bar" aria-hidden="true" />
        </button>
      </div>

      <div v-else class="msg-rail-ticks">
        <button
          v-for="item in userNavItems"
          :key="item.id"
          type="button"
          class="msg-rail-tick"
          :class="{ active: activeNavId === item.id }"
          :aria-label="item.summary"
          :aria-current="activeNavId === item.id ? 'true' : undefined"
          @click="jumpToMessage(item.id)"
        >
          <span class="msg-rail-bar" aria-hidden="true" />
        </button>
      </div>

      <div v-if="railExpanded && hoveredNavDetail" class="msg-rail-detail">
        {{ hoveredNavDetail }}
      </div>
    </nav>
    </div>

    <div v-if="actionToast" class="action-toast" role="status">{{ actionToast }}</div>

    <div v-if="shareSelectMode" class="share-action-bar chat-share-bar" @click.stop>
      <div class="share-bar-left">
        <label class="share-all">
          <input
            type="checkbox"
            :checked="shareAllSelected"
            :indeterminate.prop="shareSelectedCount > 0 && !shareAllSelected"
            @change="toggleShareSelectAll"
          />
          <span>全选</span>
        </label>
        <span class="share-count-divider" aria-hidden="true" />
        <span class="share-count">已选择 {{ shareSelectedCount }} 组对话</span>
      </div>
      <div class="share-actions-right">
        <button type="button" class="share-outline-btn" @click="exitShareSelectMode">取消</button>
        <button
          type="button"
          class="share-primary-btn"
          :disabled="!shareSelectedCount"
          @click="openShareLinkModal"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0-7.07-7.07L10 5.93"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
            <path
              d="M14 11a5 5 0 0 0-7.07 0L5.52 12.41a5 5 0 0 0 7.07 7.07L14 18.07"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
            />
          </svg>
          创建分享链接
        </button>
      </div>
    </div>

    <div
      v-if="shareLinkModalOpen"
      class="share-link-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-link-title"
      @click.self="closeShareLinkModal"
    >
      <div class="share-link-modal" @click.stop>
        <header class="share-link-modal-head">
          <h2 id="share-link-title">创建分享链接</h2>
          <button
            type="button"
            class="share-link-close"
            aria-label="关闭"
            :disabled="shareLinkCreating"
            @click="closeShareLinkModal"
          >
            ×
          </button>
        </header>
        <p class="share-link-modal-desc">
          任何获得链接的人都可以查看你分享的对话，请检查是否包含敏感或隐私内容。你可以随时在系统设置
          - 数据管理中管理被分享的链接。
        </p>
        <button
          type="button"
          class="share-link-create-btn"
          :disabled="shareLinkCreating"
          @click="createAndCopyShareLink"
        >
          {{ shareLinkCreating ? '创建中…' : '创建并复制' }}
        </button>
      </div>
    </div>

    <form
      v-if="!shareSelectMode"
      class="composer"
      :class="{ editing: !!editingId, voice: voiceMode !== 'idle', extracting }"
      @submit.prevent="send"
      @click.stop
    >
      <div v-if="editingId" class="edit-banner">
        <span>正在修改消息</span>
        <button type="button" class="link-btn" @click="cancelEdit">取消</button>
      </div>

      <div v-if="extracting" class="extract-banner" role="status">
        <span class="extract-spinner" aria-hidden="true" />
        <span>正在解析附件，请稍候（扫描件在 CPU 上可能需要数分钟）…</span>
      </div>

      <div v-if="attachments.length" class="attach-preview">
        <div
          v-for="att in attachments"
          :key="att.id"
          class="attach-chip"
          :class="att.kind"
        >
          <img v-if="att.kind === 'image' && att.previewUrl" :src="att.previewUrl" :alt="att.name" />
          <div class="attach-meta">
            <span class="attach-name">{{ att.name }}</span>
            <span class="attach-size">{{ formatFileSize(att.size) }}</span>
          </div>
          <button
            type="button"
            class="attach-remove"
            :disabled="composerBusy"
            aria-label="移除附件"
            @click="removeAttachment(att.id)"
          >
            ×
          </button>
        </div>
      </div>

      <textarea
        ref="composerRef"
        v-model="input"
        rows="2"
        :placeholder="
          extracting
            ? '正在解析附件…'
            : editingId
              ? '修改后 Enter 发送，将替换此条及之后的回复'
              : voiceMode === 'listening'
                ? '正在听写…'
                : voiceMode === 'recording'
                  ? '正在录音…'
                  : '发消息…'
        "
        :disabled="composerBusy"
        @keydown.enter.exact.prevent="send"
        @keydown.esc="editingId && cancelEdit()"
        @paste="onComposerPaste"
      />

      <div class="composer-toolbar">
        <div class="toolbar-left">
          <div class="plus-wrap">
            <button
              type="button"
              class="tool-btn"
              title="添加"
              aria-label="添加附件"
              :disabled="composerBusy"
              :aria-expanded="plusMenuOpen"
              @click="plusMenuOpen = !plusMenuOpen"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 5v14M5 12h14"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <div v-if="plusMenuOpen" class="plus-menu" role="menu">
              <button type="button" role="menuitem" @click="openFilePicker">上传附件</button>
              <button type="button" role="menuitem" @click="openImagePicker">上传图片</button>
            </div>
          </div>

          <span class="toolbar-divider" aria-hidden="true" />

          <button
            type="button"
            class="tool-chip"
            title="图片"
            :disabled="composerBusy"
            @click="openImagePicker"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect
                x="3.5"
                y="5.5"
                width="17"
                height="13"
                rx="2"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
              />
              <circle cx="9" cy="10" r="1.4" fill="currentColor" />
              <path
                d="M3.8 16.2 9 12.5l3.2 2.4 3.1-3.6 4.9 5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
            </svg>
            图片
          </button>
        </div>

        <div class="toolbar-right">
          <button
            v-if="composerBusy"
            type="button"
            class="btn stop-btn"
            @click="stopStreaming"
          >
            {{ extracting ? '停止解析' : '停止' }}
          </button>
          <button
            v-else
            type="button"
            class="mic-btn"
            :class="{ active: voiceMode !== 'idle' }"
            :title="voiceMode === 'idle' ? '语音输入' : '结束语音'"
            :aria-label="voiceMode === 'idle' ? '语音输入' : '结束语音'"
            @click="toggleVoice"
          >
            <svg v-if="voiceMode === 'idle'" viewBox="0 0 24 24" aria-hidden="true">
              <rect x="9" y="3.5" width="6" height="11" rx="3" fill="none" stroke="currentColor" stroke-width="1.7" />
              <path
                d="M6.5 11.5a5.5 5.5 0 0 0 11 0M12 17v3.5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linecap="round"
              />
            </svg>
            <svg v-else viewBox="0 0 24 24" aria-hidden="true">
              <rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor" />
            </svg>
          </button>
        </div>
      </div>

      <input
        ref="fileInputRef"
        type="file"
        class="hidden-input"
        multiple
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.csv,.zip"
        @change="onFileInputChange"
      />
      <input
        ref="imageInputRef"
        type="file"
        class="hidden-input"
        multiple
        accept="image/*"
        @change="onImageInputChange"
      />
    </form>
    </section>

    <aside
      v-if="previewAttachment"
      class="attach-detail"
      :class="{ 'image-studio': isImagePreview }"
      aria-label="附件详情"
    >
      <template v-if="isImagePreview">
        <header class="image-studio-toolbar" @click.stop>
          <div class="image-studio-left">
            <button type="button" class="studio-icon-btn" title="缩小" aria-label="缩小" @click="zoomImage(-0.2)">
              −
            </button>
            <button type="button" class="studio-icon-btn" title="放大" aria-label="放大" @click="zoomImage(0.2)">
              +
            </button>
            <span class="studio-zoom-label">{{ Math.round(imageZoom * 100) }}%</span>
          </div>
          <div class="image-studio-tools">
            <button
              v-for="tool in imageStudioTools"
              :key="tool.id"
              type="button"
              class="studio-tool-btn"
              @click="stubImageStudioTool(tool.label)"
            >
              {{ tool.label }}
            </button>
          </div>
          <div class="image-studio-right">
            <div class="studio-more-wrap">
              <button
                type="button"
                class="studio-icon-btn"
                title="更多"
                aria-label="更多"
                :aria-expanded="imageStudioMoreOpen"
                @click="imageStudioMoreOpen = !imageStudioMoreOpen"
              >
                ⋯
              </button>
              <div v-if="imageStudioMoreOpen" class="studio-more-menu" role="menu">
                <button type="button" role="menuitem" @click="extractPreviewText()">提取文字</button>
                <button
                  type="button"
                  role="menuitem"
                  @click="
                    resetImageStudioView();
                    imageStudioMoreOpen = false;
                    showToast('已重置视图')
                  "
                >
                  重置视图
                </button>
              </div>
            </div>
            <button type="button" class="studio-save-btn" @click="savePreviewImage">保存</button>
            <button type="button" class="attach-detail-close" aria-label="关闭详情" @click="closeAttachmentPreview">
              ×
            </button>
          </div>
        </header>
        <div
          class="image-studio-canvas"
          :class="{ panning: imagePanning, zoomed: imageZoom > 1 }"
          @wheel.prevent="onImageStudioWheel"
          @pointerdown="onImageStudioPointerDown"
          @pointermove="onImageStudioPointerMove"
          @pointerup="onImageStudioPointerUp"
          @pointercancel="onImageStudioPointerUp"
        >
          <img
            class="image-studio-img"
            :src="previewAttachment.previewUrl"
            :alt="previewAttachment.name"
            :style="{
              transform: `translate(${imagePanX}px, ${imagePanY}px) scale(${imageZoom})`
            }"
            draggable="false"
          />
        </div>
      </template>
      <template v-else>
        <header class="attach-detail-head">
          <div class="attach-detail-titles">
            <h2>{{ previewAttachment.name }}</h2>
            <p>
              {{ fileTypeLabel(previewAttachment) }}
              <template v-if="previewAttachment.size > 0">
                · {{ formatFileSize(previewAttachment.size) }}
              </template>
            </p>
          </div>
          <div class="attach-detail-actions">
            <button
              v-if="isExtractableAttachment(previewAttachment)"
              type="button"
              class="extract-text-btn"
              :disabled="extractingPreviewText || composerBusy"
              @click="extractPreviewText()"
            >
              {{ extractingPreviewText ? '提取中…' : '提取文字' }}
            </button>
            <button type="button" class="attach-detail-close" aria-label="关闭详情" @click="closeAttachmentPreview">
              ×
            </button>
          </div>
        </header>
        <div class="attach-detail-body">
          <PdfPreview
            v-if="isPdfAttachment(previewAttachment) && previewAttachment.previewUrl"
            :url="previewAttachment.previewUrl"
          />
          <div
            v-else-if="previewAttachment.kind === 'audio' && previewAttachment.previewUrl"
            class="attach-detail-audio"
          >
            <audio :src="previewAttachment.previewUrl" controls />
          </div>
          <div v-else class="attach-detail-fallback">
            <div class="msg-att-icon large" :data-ext="fileExt(previewAttachment.name) || 'FILE'">
              {{ fileExt(previewAttachment.name).slice(0, 3) || 'FILE' }}
            </div>
            <p>
              {{
                isPdfAttachment(previewAttachment) && !previewAttachment.previewUrl
                  ? '附件未持久化，无法预览（请重新上传）'
                  : '当前格式暂不支持内嵌预览'
              }}
            </p>
            <a
              v-if="previewAttachment.previewUrl"
              class="attach-detail-open"
              :href="previewAttachment.previewUrl"
              :download="previewAttachment.name"
              target="_blank"
              rel="noopener"
            >
              下载文件
            </a>
          </div>
        </div>
      </template>
    </aside>
    </div>
  </div>
</template>

